/**************************
 * Service used for shared logic between ProFlow, PIA contract flow and LouiseV2
 * Aimed towards usage for Quotes API.
 **************************/

import { isEqual } from 'lodash';
import { Condition, ConditionsObject } from '../../../../types/ProductFactory';
import { QuoteQuestionAnswer } from '../../../../types/QuoteQuestion';
import i18n from '../../../../i18n';

/**
 * This function evaluates the conditions against the given answers. It checks 2 things:
 * - If the question should be shown or not if another question is already shown
 * - If the question should be shown based on the answer of another question
 * These things are both AND-ed. The first check is also AND-ed but the second one is OR'ed:
 * (... AND ... AND ...) AND (... OR ... OR ...)
 * If the first check is not included, it only checks the second check:
 * (... OR ... OR ...)
 * @param answers The answers related to the questionnaire that are needed for checking the condition
 * @param conditions The conditions that need to be evaluated
 */
export const handleLegacyCondition = (
  answers: QuoteQuestionAnswer[],
  conditions?: Condition[],
): boolean => {
  return (conditions || []).length > 0
    ? Boolean(
        ((conditions?.filter(({ value }) => value === null) || []).length ===
          0 ||
          conditions
            ?.filter(({ value }) => value === null)
            .every((condition) => {
              const answer = answers.find(
                ({ id }) => id === condition?.question_id,
              );
              if (!answer) return condition?.value === null;
              return false;
            })) &&
          conditions
            ?.filter(({ value }) => value !== null)
            .some((condition) => {
              const answer = answers.find(
                ({ id }) => id === condition?.question_id,
              );
              if (!answer) return false;
              return answer.answer === condition?.value;
            }),
      )
    : true;
};

/**
 * This function checks if the condition is  true or not.
 * - A value NULL checks on existance of an answer, if it's not there, the question can be shown.
 * - An actual value will check if the value is equals, if it is, the question can be shown.
 * @param answers The answers related to the questionnaire that are needed for checking the condition
 * @param condition The condition that need to be evaluated
 * @returns Boolean if it the condition is correct or not
 */
export const handleSingleCondition = (
  answers: QuoteQuestionAnswer[],
  condition: Condition,
  index?: number,
): boolean => {
  const answer = answers.find(({ id }) => id === condition?.question_id);

  // If BOTH have an index
  // AND the index is not the same, skip any condition handling
  if (answer?.index && index && index !== answer?.index) return true;

  let initialReturn = false;

  // Check for NULL conditino
  if (condition.value == null && !answer) initialReturn = true;
  // Check for VALUE condition
  else if (Array.isArray(answer?.answer)) {
    // When value and answer are both arrays, they should be equal
    if (
      Array.isArray(condition?.value) &&
      isEqual(answer?.answer, condition?.value)
    )
      initialReturn = true;
    // When answer is an array and the condition value is a primitive, the answer array should include the condition value
    else if (answer?.answer.includes(condition?.value)) initialReturn = true;
  } else if (isEqual(answer?.answer, condition?.value)) initialReturn = true;

  // If none apply, return false
  if (condition.inversed) return !initialReturn;
  return initialReturn;
};

/**
 * This wille handle the conditions depending on the type that is given.
 * @param answers The answers related to the questionnaire that are needed for checking the condition
 * @param conditionsObject The conditions that need to be evaluated
 * @returns Boolean if the question should be shown
 */
export const handleConditions = (
  answers: QuoteQuestionAnswer[],
  conditionsObject?: ConditionsObject,
  index?: number,
): boolean => {
  const { items, type } = conditionsObject || {};
  if ((items || []).length === 0) return true;

  if (type?.toLowerCase() === 'and') {
    return !!items?.every((condition) => {
      if ('items' in condition) {
        // recursive function call
        return handleConditions(answers, condition, index);
      }
      return handleSingleCondition(answers, condition, index);
    });
  } else if (type?.toLowerCase() === 'or') {
    return !!items?.some((condition) => {
      if ('items' in condition) {
        // recursive function call
        return handleConditions(answers, condition, index);
      }
      return handleSingleCondition(answers, condition, index);
    });
  }
  console.error(`Unsupported condition type ${conditionsObject?.type}`);
  return true;
};

export const chooseConditionHandling = (
  answers: QuoteQuestionAnswer[],
  unknownConditionsType?: ConditionsObject | Condition[],
  index?: number,
): boolean => {
  if (Array.isArray(unknownConditionsType))
    return handleLegacyCondition(answers, unknownConditionsType);
  else if (typeof unknownConditionsType === 'object')
    return handleConditions(answers, unknownConditionsType, index);
  return true;
};

export const getQuotesErrorMessage = (code: number, msg?: string): string => {
  if (msg) return msg;

  switch (code) {
    case 422:
      return i18n.t('No acceptation by the company');
    case 503:
      return i18n.t('No connection with the insurer API');
    default:
      return i18n.t('FLOWS_QUOTES_ERROR-MESSAGE_DEFAULT');
  }
};
